/**
 * Full CMS fallbacks (Phase 2K-Final).
 * Inline `labels` + `features` sections are read by page components.
 * Block sections render via UniversalPageRenderer when layout=document.
 */
import { inlineSection, section } from "./cms-helpers";

const L = (obj) => inlineSection("labels", "json", JSON.stringify(obj));
const F = (arr) => inlineSection("features", "json", JSON.stringify(arr));

export const SACRED_PAGES_DEFAULTS = {
  home: {
    slug: "home",
    title: "Discover Your Dharma",
    metadata: { tags: ["landing"], icon: "home" },
    sections: [
      L({
        heroTitle: "Discover Your",
        heroHighlight: "Dharma",
        heroSubtitle: "वेदपथ — Your personalized journey through ancient Vedic wisdom",
        heroBody:
          "Uncover your spiritual archetype, understand your karmic gates, and receive guidance on your unique path to liberation.",
        ctaPrimary: "Begin Your Journey",
        ctaSecondary: "Explore the System",
        featuresTitle: "The VedaPath System",
        featuresSubtitle:
          "A comprehensive framework rooted in Vedic wisdom, designed to guide seekers at every stage of their spiritual journey.",
        ctaBlockTitle: "Ready to Begin?",
        ctaBlockBody:
          "Take the Dharma Diagnostic to discover your archetype, understand your gates, and receive personalized guidance for your spiritual journey.",
        ctaDiagnostic: "Take the Diagnostic",
        ctaSignup: "Create Free Account",
        mobileBannerTitle: "Mobile App Coming Soon",
        mobileBannerBody: "Practice sādhana, view pañchāṅga, and connect with Āchārya on the go.",
      }),
      F([
        { icon: "User", title: "14 Archetypes", desc: "Discover your spiritual blueprint from Survivor to Witness", color: "text-primary", link: "/knowledge" },
        { icon: "Shield", title: "12 Karmic Gates", desc: "Understand the obstacles that shape your journey", color: "text-secondary-600", link: "/knowledge?section=gates" },
        { icon: "Target", title: "4 Adhikāra Levels", desc: "Know your readiness for different practices", color: "text-primary-400", link: "/knowledge?section=adhikara" },
        { icon: "Compass", title: "4 Pathways", desc: "Bhakti, Karma, Jñāna, and Rāja Yoga", color: "text-secondary", link: "/knowledge?section=pathways" },
        { icon: "Calendar", title: "Pañchāṅga", desc: "Vedic calendar with tithi, nakshatra, and sacred events", color: "text-primary", link: "/panchanga" },
        {
          icon: "MessageCircle",
          title: "Dharma Āchārya AI",
          desc: "Personalized guidance for your spiritual path — based on your archetype and curriculum",
          color: "text-secondary-600",
          link: "/guide",
        },
      ]),
      inlineSection("hero_image_url", "text", "https://images.unsplash.com/photo-1617634667039-8e4cb277ab46?w=1920", {
        display: "inline",
      }),
    ],
  },
  "knowledge-base": {
    slug: "knowledge-base",
    title: "Knowledge Base",
    metadata: { tags: ["knowledge"] },
    sections: [
      L({
        pageTitle: "Knowledge Base",
        pageSubtitle: "Explore the complete VedaPath Dharma System",
        emptyTopics: "Select a category to explore topics.",
        tabArchetypes: "14 Archetypes",
        tabGates: "12 Gates",
        tabAdhikara: "4 Adhikāra",
        tabPathways: "6 Pathways",
        tabCurriculum: "Curriculum",
        tabTopics: "Topics",
      }),
    ],
  },
  archetypes: {
    slug: "archetypes",
    title: "Vedic Archetypes",
    metadata: { tags: ["knowledge"] },
    sections: [L({ intro: "Twelve archetypes reflect how dharma expresses through your temperament and life season." })],
  },
  "karmic-gates": {
    slug: "karmic-gates",
    title: "Karmic Gates",
    metadata: { tags: ["knowledge"] },
    sections: [L({ intro: "Twelve gates mark thresholds of growth — each with its lesson, practice, and blessing." })],
  },
  "adhikara-levels": {
    slug: "adhikara-levels",
    title: "Adhikāra Levels",
    metadata: { tags: ["knowledge"] },
    sections: [L({ intro: "Four levels of spiritual qualification — from curiosity to readiness for deeper transmission." })],
  },
  pathways: {
    slug: "pathways",
    title: "Four Pathways",
    metadata: { tags: ["knowledge"] },
    sections: [L({ intro: "Bhakti, Jñāna, Karma, and Rāja — four classical pathways integrated for modern seekers." })],
  },
  "prayer-hall": {
    slug: "prayer-hall",
    title: "Dhyāna Mandira — Prayer Hall",
    metadata: { tags: ["practice"], icon: "prayer" },
    sections: [
      L({
        sanskritTitle: "ध्यान मन्दिर",
        pageTitle: "Dhyāna Mandira — Prayer Hall",
        pageSubtitle: "A sacred space for breath, mantra, and steady presence.",
        sessionDuration: "Session Duration",
        currentState: "Your Current State",
        stateHint: "Select how you're feeling for adaptive guidance",
        myMantras: "My Mantras",
        mantrasHint: "Use one of your saved mantras for japa sessions, or practice without one.",
        none: "None",
        selectedMantra: "Selected mantra for this session.",
        recommended: "Recommended",
        defaultMin: "Default: {n} min",
        begin: "Begin",
        footerShanti: "ॐ सर्वे भवन्तु सुखिनः — May all beings be happy",
        sacredSound: "Sacred Sound",
        enableSound: "Enable sound",
        play: "Play",
        pause: "Pause",
        loopAudio: "Loop audio",
        yourMantra: "Your Mantra",
        practiceInstructions: "Practice Instructions",
        breathingPatterns: "Breathing Patterns",
        resume: "Resume",
        endSession: "End Session",
      }),
    ],
  },
  sadhana: {
    slug: "sadhana",
    title: "Sādhana",
    metadata: { tags: ["practice"] },
    sections: [
      L({
        pageTitle: "Sādhana",
        pageSubtitle: "Track. Guide. Continue.",
        tabPractice: "Practice",
        tabAnalytics: "Analytics",
        globalParticipation: "Global participation today",
        logToday: "Log today's sādhana",
        practiceLabel: "Practice",
        minutes: "Minutes",
        notesOptional: "Notes (optional)",
        log: "Log",
        signInToLog: "Sign in to log and sync your sādhana across devices.",
        dashboard: "Dashboard",
        totalMinutes: "Total minutes",
        daysPracticed: "Days practiced",
        currentStreak: "Current streak",
        mantrasOpen: "Mantras (no guru needed)",
        myMantras: "My Mantras",
        mantrasGuru: "Mantras requiring guru upadeśa",
        guidelines: "Guidelines",
        festivalSadhana: "Festival sādhana",
        saveMantra: "Save mantra",
        noMantras: "No saved mantras yet. Add one below.",
        delete: "Delete",
        daily: "Daily",
        weekly: "Weekly",
        seasonal: "Seasonal",
        mindset: "Mindset",
        commonMistakes: "Common mistakes",
        integrationDuties: "Integration with duties",
      }),
    ],
  },
  satsanga: {
    slug: "satsanga",
    title: "Satsaṅga",
    metadata: { tags: ["community"] },
    sections: [
      L({
        pageTitle: "Satsaṅga",
        pageSubtitle: "Weekly reflection in good company.",
        audienceAll: "All",
        audienceChildren: "Children",
        audienceElders: "Elders",
        audienceStruggling: "Struggling",
        deepeningQuestions: "Deepening Questions",
        myReflection: "My reflection",
        reflectionPlaceholder: "Write what comes up. Honest beats perfect.",
        save: "Save",
        savedReflection: "Saved to your reflections.",
        signInReflection: "Sign in to save reflections.",
      }),
    ],
  },
  "acharya-ai": {
    slug: "acharya-ai",
    title: "Āchārya AI",
    metadata: { tags: ["ai"], icon: "acharya" },
    sections: [
      L({
        pageTitle: "Āchārya AI",
        pageSubtitle: "Your digital Vedic teacher on the spiritual path",
        pageNote: "Personalized guidance based on your archetype and curriculum",
        seekerTone: "Seeker tone",
        emptyTitle: "Seek Guidance from Your Āchārya",
        emptyBody:
          "Ask questions about archetypes, gates, pathways, or seek scriptural guidance on your journey.",
        inputPlaceholder: "Seek guidance from your Āchārya…",
        errorMessage: "I apologize, but I encountered an error. Please try again.",
        seekerStates: [
          { id: "innocent", label: "Innocent" },
          { id: "curious", label: "Curious" },
          { id: "confused", label: "Confused" },
          { id: "hurting", label: "Hurting" },
          { id: "disciplined", label: "Disciplined" },
          { id: "knowledge", label: "Knowledge" },
          { id: "mature", label: "Mature" },
        ],
        fallbackSuggestions: ["What is my spiritual archetype?", "How can I work through fear?"],
      }),
    ],
  },
  "dashboard-cards": {
    slug: "dashboard-cards",
    title: "Dashboard",
    metadata: { tags: ["dashboard"] },
    sections: [
      L({
        signInTitle: "Please sign in to view your dashboard",
        signIn: "Sign In",
        noDiagnosticTitle: "Complete Your Diagnostic",
        noDiagnosticBody: "Take the Dharma Diagnostic to unlock your personalized dashboard.",
        beginDiagnostic: "Begin Diagnostic",
        welcome: "Welcome, {name}",
        welcomeSubtitle: "Your personalized dharma dashboard",
        dailySadhana: "Daily Sādhana",
        dharmaTip: "Dharma Tip",
        festival: "Festival",
        reading: "Reading",
        seekerState: "Seeker State",
        mindToday: "Your Mind Today",
        dharmaPriority: "Dharma Priority Today",
        prayerHall: "Prayer Hall",
        open: "Open",
        go: "Go",
        today: "Today:",
        tomorrow: "Tomorrow:",
        noFestival: "No major festival at sunrise today.",
        tone: "Tone:",
        nextSteps: "Next Steps for You",
        continueReading: "Continue Reading",
        myJourney: "My VedaPath Journey",
        seekerInsight: "Seeker state insight",
        suggested: "Suggested:",
        gentlerSeason: "A gentler season is suggested for your current state.",
        sadhanaConsistency: "Sādhana consistency:",
        coreNeed: "Core Need",
        naturalSpiral: "Natural Spiral",
        currentStage: "Current Stage:",
        focusAreas: "Focus Areas",
        adhikaraLevel: "Adhikāra Level",
        recommendedPathways: "Recommended Pathways",
        karmicGates: "Karmic Gates to Work Through",
        primary: "Primary",
        secondary: "Secondary",
        antidote: "Antidote:",
        consultAcharya: "Consult Āchārya AI",
        knowledgeBase: "Knowledge Base",
        retakeDiagnostic: "Retake Diagnostic",
        panchangaNote: "Today's tithi, nakshatra, and sacred observances appear in the Pañchāṅga card.",
      }),
    ],
  },
  "help-now": {
    slug: "help-now",
    title: "Crisis Guidance",
    metadata: { tags: ["crisis"] },
    sections: [
      L({
        pageTitle: "Crisis Guidance",
        disclaimer:
          "Spiritual orientation — not emergency services. Call local emergency if you are in danger.",
        immediateSteps: "Immediate steps",
        dharmaPrinciple: "Dharma principle:",
        whatNotToDo: "What NOT to do",
        oneMinute: "1 minute",
        tenMinutes: "10 minutes",
        thirtyMinutes: "30 minutes",
      }),
    ],
  },
  about: {
    slug: "about",
    title: "About VedaPath",
    metadata: { tags: ["info"] },
    sections: [
      section(
        "body",
        "markdown",
        "VedaPath brings authentic Vedic frameworks — archetypes, gates, adhikāra, pathways, pañchāṅga, and Āchārya guidance — into a calm, modern experience for seekers worldwide.\n\nOur mission is to make dharma practical: discover your temperament, understand thresholds of growth, and practice steadily with community and scripture."
      ),
    ],
  },
  contact: {
    slug: "contact",
    title: "Contact",
    metadata: { tags: ["info"] },
    sections: [
      section("intro", "text", "Reach the VedaPath team for support, partnerships, and feedback."),
      section("email", "text", "contact.vedapath@gmail.com"),
    ],
  },
};
